import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { BellRing, MessageCircle, Check, Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LeadFollowups({ refreshKey, onChanged }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState('');
    const load = () => axios.get(`${API}/admin/leads/followups`).then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const markNudged = async (id) => {
        setBusy(id);
        try { await axios.patch(`${API}/admin/leads/${id}/nudged`); toast.success('Marked as nudged — next reminder in 3 days'); load(); onChanged?.(); }
        catch { toast.error('Could not update'); }
        setBusy('');
    };
    const digest = async () => {
        setBusy('digest');
        try { const { data: r } = await axios.post(`${API}/admin/leads/followups/digest`); toast[r.sent ? 'success' : 'info'](r.sent ? `Digest sent to your Telegram (${r.due} leads)` : (r.reason || 'No leads due right now')); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
        setBusy('');
    };

    if (!data) return null;
    const leads = data.leads || [];
    return (
        <div className="rounded-2xl p-5 mb-8" style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.10), rgba(26,26,34,0.9))', border: '1px solid var(--border-highlight)' }} data-testid="lead-followups-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div className="flex items-center gap-2">
                    <span className="icon-bubble-gold w-9 h-9 rounded-xl flex items-center justify-center"><BellRing className="w-4 h-4" style={{ color: '#FFD700' }} /></span>
                    <div>
                        <p className="font-black text-sm" style={{ color: 'var(--text-primary)', fontFamily: "'Outfit', sans-serif" }}>Follow-up due · {leads.length}</p>
                        <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>Contacted {data.after_days}+ days ago and still not registered. A digest also lands in your Telegram every morning.</p>
                    </div>
                </div>
                <Button size="sm" variant="outline" onClick={digest} disabled={!!busy} data-testid="followup-digest-btn">{busy === 'digest' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send digest to Telegram</Button>
            </div>
            {leads.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="followup-empty">Nothing due right now. Leads you mark as Contacted appear here automatically after {data.after_days} days.</p>
            ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                    {leads.map((l) => (
                        <div key={l.id} className="rounded-xl p-3 flex items-center gap-3 premium-card" data-testid={`followup-${l.id}`}>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{l.is_hot && '🔥 '}{l.name} <span className="text-[10px] font-black ms-1" style={{ color: 'var(--text-muted)' }}>{l.language.toUpperCase()}</span></p>
                                <p className="text-[11px] font-mono" style={{ color: 'var(--text-muted)' }}>{l.phone} · contacted {new Date(l.contacted_at).toLocaleDateString()}{l.nudge_count ? ` · nudged ×${l.nudge_count}` : ''}</p>
                            </div>
                            <a href={l.nudge_wa_link} target="_blank" rel="noopener noreferrer" data-testid={`followup-wa-${l.id}`}><Button size="sm" className="bg-[#25D366] hover:bg-[#1fb958] text-white h-8"><MessageCircle className="w-3.5 h-3.5 mr-1" />Nudge</Button></a>
                            <Button size="sm" variant="ghost" className="h-8" onClick={() => markNudged(l.id)} disabled={busy === l.id} title="Mark nudged" data-testid={`followup-done-${l.id}`}><Check className="w-4 h-4" /></Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
